/*
 * femme featured blog matches V1.
 * Loads a short filtered archive for each _blog: tag. Never scans the first
 * 50 articles to decide eligibility. Preserves server-rendered fallback cards
 * if the tagged endpoint cannot be fetched.
 */
(() => {
  'use strict';

  const configSelector = '[data-femme-blog-tag-config]';
  const matchSelector = '[data-femme-blog-tag-feed]';
  const articleSelector = '[data-femme-blog-article-url]';
  const responseCache = new Map();

  function fetchMatches(path) {
    const url = new URL(path, window.location.origin);
    if (url.origin !== window.location.origin) return Promise.resolve([]);
    url.searchParams.set('section_id', 'femme-blog-tag-feed');
    const key = url.toString();

    if (!responseCache.has(key)) {
      responseCache.set(key,
        fetch(key, { credentials: 'same-origin' })
          .then((response) => response.ok ? response.text() : '')
          .then((html) => {
            if (!html) return [];
            const responseDocument = new DOMParser().parseFromString(html, 'text/html');
            const feed = responseDocument.querySelector(matchSelector);
            if (!feed) return [];
            return Array.from(feed.querySelectorAll(articleSelector))
              .map((element) => ({
                url: element.dataset.femmeBlogArticleUrl,
                published: Number(element.dataset.femmeBlogPublished || 0),
                markup: element.outerHTML
              }))
              .filter((candidate) => candidate.url);
          })
          .catch(() => [])
      );
    }
    return responseCache.get(key);
  }

  async function initialise(configElement) {
    if (configElement.dataset.femmeBlogInitialised === 'true') return;
    configElement.dataset.femmeBlogInitialised = 'true';

    const section = configElement.closest('.featured-blog');
    const grid = section && section.querySelector('[data-femme-blog-items]');
    if (!grid) return;

    let config;
    try {
      config = JSON.parse(configElement.textContent);
    } catch (_error) {
      return;
    }
    const blogUrls = Array.isArray(config.blogs) ? config.blogs : [];
    const tags = Array.isArray(config.tags) ? config.tags : [];
    const limit = Math.max(1, Math.min(8, Number(config.limit) || 3));
    if (!blogUrls.length || !tags.length) return;

    const originalCards = Array.from(grid.children)
      .filter((element) => element.classList.contains('f-column'))
      .map((element) => ({
        url: element.querySelector('.article-card__title a')?.getAttribute('href') || '',
        published: 0,
        element
      }))
      .filter((item) => item.url);

    const requests = [];
    for (const blogPath of blogUrls) {
      if (typeof blogPath !== 'string' || !blogPath.startsWith('/')) continue;
      for (const tagHandle of tags) {
        if (typeof tagHandle !== 'string' || !tagHandle.startsWith('blog-')) continue;
        requests.push(fetchMatches(blogPath.replace(/\/$/, '') + '/tagged/' + encodeURIComponent(tagHandle)));
      }
    }
    if (!requests.length) return;

    const returnedGroups = await Promise.all(requests);
    const uniqueMatches = new Map();
    for (const candidate of returnedGroups.flat()) {
      if (!uniqueMatches.has(candidate.url) || candidate.published > uniqueMatches.get(candidate.url).published) {
        uniqueMatches.set(candidate.url, candidate);
      }
    }
    if (uniqueMatches.size === 0) return;

    const matches = Array.from(uniqueMatches.values())
      .sort((a, b) => b.published - a.published || a.url.localeCompare(b.url));

    const chosen = [];
    const usedUrls = new Set();
    for (const candidate of matches.concat(originalCards)) {
      if (usedUrls.has(candidate.url)) continue;
      usedUrls.add(candidate.url);
      chosen.push(candidate);
      if (chosen.length >= limit) break;
    }
    if (chosen.length === originalCards.length &&
        chosen.every((item, index) => item.url === originalCards[index].url)) return;

    const updated = chosen.map((item) => {
      if (item.element) return item.element;
      const fragment = document.createElement('template');
      fragment.innerHTML = item.markup;
      const element = fragment.content.firstElementChild;
      if (element && !config.showDate) {
        const date = element.querySelector('.article-card__date');
        if (date) date.remove();
      }
      return element;
    }).filter(Boolean);

    if (updated.length) grid.replaceChildren(...updated);
  }

  function scan(root) {
    if (!root || !root.querySelectorAll) return;
    for (const configElement of root.querySelectorAll(configSelector)) {
      initialise(configElement);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  } else {
    scan(document);
  }
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
